export * from './connexis';

import { Ref } from '@vue/composition-api';
import axios, { AxiosRequestConfig } from 'axios';
import {
    TrainingPlanClient,
    ScopeClient,
    UnitStandardClient,
    UnitStandardVersionClient,
    ResultClient,
    TrainingPlanUnitStandardClient,
    AccountClient,
    AssessorClient,
} from './connexis';

const axiosInstance = axios.create({
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
    },
    responseType: 'json',
});

axiosInstance.interceptors.request.use((config) => {
    const method = config.method?.toLowerCase();

    // Prevent stale cached GET responses across environments/browsers.
    // The unique timestamp makes the URL distinct on every call, bypassing
    // browser/CDN caches even when the server returns cacheable response headers.
    if (method === 'get') {
        config.params = {
            ...(config.params || {}),
            _: Date.now(),
        };
    }

    return config;
});

const TrainingPlanClientInstance = new TrainingPlanClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);

const ScopeClientInstance = new ScopeClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);

const UnitStandardClientInstance = new UnitStandardClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);

const UnitStandardVersionClientInstance = new UnitStandardVersionClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);

const ResultClientInstance = new ResultClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);
const TrainingPlanUnitStandardClientInstance =
    new TrainingPlanUnitStandardClient(
        process.env.VUE_APP_API_BASE_URI,
        axiosInstance,
    );

// Backward-compatible alias for existing imports/usages
const TraineeClientinstance = TrainingPlanClientInstance;

const AccountClientInstance = new AccountClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);

const AssessorClientInstance = new AssessorClient(
    process.env.VUE_APP_API_BASE_URI,
    axiosInstance,
);

const useAxiosTokenInterceptor = (accessToken: Ref<string>): void => {
    axiosInstance.interceptors.request.use(
        (config) => {
            if (accessToken.value) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                config.headers![
                    'Authorization'
                ] = `Bearer ${accessToken.value}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        },
    );
};

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

// Concurrent requests that all 401 at once (e.g. an expired token) should
// trigger a single token refresh, not one per request.
let refreshPromise: Promise<void> | null = null;

// Refreshes the access token and retries the original request once when the
// API returns a 401 (expired/invalid token), instead of surfacing nothing.
const useAxiosUnauthorizedRetryInterceptor = (
    refreshToken: () => Promise<void>,
): void => {
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error?.config as
                | RetryableRequestConfig
                | undefined;

            if (
                error?.response?.status !== 401 ||
                !originalRequest ||
                originalRequest._retry
            ) {
                return Promise.reject(error);
            }
            originalRequest._retry = true;

            try {
                if (!refreshPromise) {
                    refreshPromise = refreshToken().finally(() => {
                        refreshPromise = null;
                    });
                }
                await refreshPromise;
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }

            return axiosInstance(originalRequest);
        },
    );
};

export {
    axiosInstance,
    useAxiosTokenInterceptor,
    useAxiosUnauthorizedRetryInterceptor,
    TrainingPlanClientInstance,
    ScopeClientInstance,
    UnitStandardClientInstance,
    UnitStandardVersionClientInstance,
    ResultClientInstance,
    TrainingPlanUnitStandardClientInstance,
    AccountClientInstance,
    AssessorClientInstance,
    TraineeClientinstance,
};
